import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CloseIcon from '@mui/icons-material/Close';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { List } from 'react-window';
import { useData } from './DataProvider';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { useTheme } from '@mui/material/styles';
import ResizeHandle from './components/ResizeHandle';

const columns = [
  { id: 'ActionTime', label: 'Time', minWidth: 80, maxWidth: 120 }, // Move time column to first
  { id: 'ProcessName', label: 'Process', minWidth: 80, maxWidth: 120 },
  { id: 'Action', label: 'Action', minWidth: 80, maxWidth: 120 },
  { id: 'EventType', label: 'Type', minWidth: 70, maxWidth: 100 },
  { id: 'ItemName', label: 'Item Name', minWidth: 120, maxWidth: 260 },
  { id: 'Details', label: 'Details', minWidth: 120, maxWidth: 400 },
];

// Constants for virtualization
const ROW_HEIGHT = 24;
const BUFFER_SIZE = 10; // Number of items to render outside the visible area

// Memoized click image lookup to avoid O(n) find on every render
const MemoizedClickImage = React.memo(({ selectedEvent, mouseClickRowsWithImages, clickimagesPath, deferImageLoad }) => {
  const clickImageFileName = useMemo(() => {
    if (!selectedEvent) return null;
    // First check if selectedEvent already has clickImageFileName
    if (selectedEvent.clickImageFileName) {
      return selectedEvent.clickImageFileName;
    }
    // If not, look it up in mouseClickRowsWithImages by VideoOffset
    if (typeof selectedEvent.VideoOffset === 'number' && mouseClickRowsWithImages?.length) {
      const matchingEvent = mouseClickRowsWithImages.find(
        e => e.VideoOffset === selectedEvent.VideoOffset
      );
      return matchingEvent?.clickImageFileName || null;
    }
    return null;
  }, [selectedEvent, mouseClickRowsWithImages]);

  if (!clickImageFileName || !clickimagesPath) return null;
  
  return (
    <ClickImageDisplay 
      clickImageFileName={clickImageFileName}
      clickimagesPath={clickimagesPath}
      deferLoad={deferImageLoad}
    />
  );
});

// Component to handle Firebase click image loading
// When deferLoad=true, shows a button to load on demand instead of auto-loading
const ClickImageDisplay = ({ clickImageFileName, clickimagesPath, deferLoad = false }) => {
  const [imageUrl, setImageUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [userRequested, setUserRequested] = React.useState(false);

  const loadImage = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (clickimagesPath) {
        // Firebase Storage - generate download URL for specific image
        const firebaseImagePath = `${clickimagesPath}/${clickImageFileName}.jpg`;

        try {
          const imageRef = ref(storage, firebaseImagePath);
          const url = await getDownloadURL(imageRef);
          if(url) {
            // only if exists: (getDownloadURL should throw an error)
            setImageUrl(url);
          }
        } catch (error) {
          if (error.code === 'storage/object-not-found') {
            setError('Image does not exist (yet)');
          } else if (error.code === 'storage/unauthorized') {
            setError('Permission denied');
          } else {
            setError(error.message || 'Failed to load image');
          }
        }
      } else {
        setError('No Firebase clickimages path provided');
      }
    } catch (err) {
      console.error('Error loading click image:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clickImageFileName, clickimagesPath]);

  // Auto-load if not deferred, or if user requested
  React.useEffect(() => {
    if (clickImageFileName && (!deferLoad || userRequested)) {
      loadImage();
    }
  }, [clickImageFileName, deferLoad, userRequested, loadImage]);

  // Reset state when filename changes
  React.useEffect(() => {
    setImageUrl(null);
    setError(null);
    setUserRequested(false);
  }, [clickImageFileName]);

  const handleLoadClick = () => {
    setUserRequested(true);
  };

  // Show load button if deferred and not yet requested
  if (deferLoad && !userRequested && !imageUrl) {
    return (
      <Box mt={0.5} mb={0.5}>
        <Tooltip title="Load screenshot from Firebase Storage">
          <IconButton
            size="small"
            onClick={handleLoadClick}
            sx={{ 
              border: 1, 
              borderColor: 'divider', 
              borderRadius: 0.5,
              px: 1,
              py: 0.25,
              fontSize: 11,
              color: 'primary.main',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <Typography variant="body2" sx={{ fontSize: 11, mr: 0.5 }}>
              Load Screenshot
            </Typography>
            📷
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box mt={1} mb={1}>
      {loading && <Typography variant='body2'>Loading screenshot...</Typography>}
      {error && <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: 12 }}>{error}</Typography>}
      {imageUrl && !loading && !error && (
        <Box
          component="img"
          src={imageUrl}
          alt={`Click screenshot ${clickImageFileName}`}
          sx={{
            maxWidth: '100%',
            maxHeight: '200px',
            border: '1px solid #ddd',
            borderRadius: 1,
            objectFit: 'contain',
            display: 'block'
          }}
          onError={(e) => {
            console.error('Image failed to load:', imageUrl);
            setError('Failed to display image');
          }}
        />
      )}
    </Box>
  );
};

// Virtualized Table Row Component - optimized to avoid re-renders on selection change
const VirtualTableRow = React.memo(({ index, style, ...rowProps }) => {
  const { displayRows, selectedIdxRef, onSelect, handleContextMenu, columns, formatOffset, colors } = rowProps;
  const row = displayRows[index];
  
  // Use local state to track selection - only this row re-renders when its selection changes
  const [isSelected, setIsSelected] = React.useState(() => selectedIdxRef.current === index);
  
  // Subscribe to selection changes via the ref's callback list
  React.useEffect(() => {
    const checkSelection = () => {
      const shouldBeSelected = selectedIdxRef.current === index;
      setIsSelected(prev => prev !== shouldBeSelected ? shouldBeSelected : prev);
    };
    // Register this row's check function
    if (selectedIdxRef.listeners) {
      selectedIdxRef.listeners.add(checkSelection);
    }
    // Initial check
    checkSelection();
    return () => {
      if (selectedIdxRef.listeners) {
        selectedIdxRef.listeners.delete(checkSelection);
      }
    };
  }, [selectedIdxRef, index]);
  
  if (!row) return null;

  const backgroundColor = isSelected ? colors.selectedBg : 'transparent';

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        backgroundColor,
        borderBottom: `1px solid ${colors.divider}`,
        boxSizing: 'border-box',
      }}
      onClick={() => onSelect(index)}
    >
      {columns.map((col, colIndex) => {
        const v = row[col.id];
        const displayValue = col.id === 'ActionTime'
          ? (typeof row.VideoOffset === 'number' && !isNaN(row.VideoOffset)
            ? formatOffset(row.VideoOffset)
            : '')
          : (v != null ? String(v) : '');

        return (
          <div
            key={col.id}
            title={displayValue}
            style={{
              padding: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: col.id === 'Details' ? '25%' : col.id === 'ItemName' ? '20%' : '11%',
              borderRight: colIndex < columns.length - 1 ? `1px solid ${colors.divider}` : 'none',
              display: 'flex',
              alignItems: 'center',
              height: ROW_HEIGHT,
              boxSizing: 'border-box',
              color: colors.textPrimary,
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            }}
            onContextMenu={(e) => {
              if (v != null && v !== '') {
                handleContextMenu(e, col.id, v);
              }
            }}
          >
            {displayValue}
          </div>
        );
      })}
    </div>
  );
});

const WatchLogViewer = ({ appInfo }) => {
  const theme = useTheme();
  const {
    displayRows,
    allRows,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    selectedIdx,
    setSelectedIdx,
    selectedEvent,
    mouseClickRowsWithImages,
    showOnlyCurrentSecond,
    setShowOnlyCurrentSecond,
    setSeekTo,
    loading,
    loadingProgress,
    error,
    addFilter,
    showEventDetailsPanel,
    setShowEventDetailsPanel,
    hugeLogMode, // Defer image loading for large files
  } = useData();

  const [contextMenu, setContextMenu] = React.useState(null); // { mouseX, mouseY, column, value }
  const [detailsWidth, setDetailsWidth] = React.useState(420);
  const listRef = useRef(null);
  
  // Selection ref with listener pattern - allows rows to subscribe without causing full re-renders
  const selectedIdxRef = useRef(selectedIdx);
  selectedIdxRef.listeners = selectedIdxRef.listeners || new Set();
  
  // Update ref and notify only affected rows when selection changes
  useEffect(() => {
    selectedIdxRef.current = selectedIdx;
    // Notify all subscribed rows to check their selection state
    selectedIdxRef.listeners.forEach(listener => listener());
  }, [selectedIdx]);

  // Helper to format VideoOffset as hh:mm:ss.ms (hide hours if 0)
  const formatOffset = useCallback((ms) => {
    if (typeof ms !== 'number' || isNaN(ms)) return '';
    const sign = ms < 0 ? '-' : '';
    const absMs = Math.abs(ms);
    const totalSeconds = Math.floor(absMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = Math.round(absMs % 1000);
    
    // Format time string - only show hours if > 0
    if (hours > 0) {
      return `${sign}${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
    } else {
      return `${sign}${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
    }
  }, []);

  // Context menu handlers
  const handleContextMenu = useCallback((event, column, value) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      column,
      value,
    });
  }, []);

  const handleCloseMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleAddFilter = useCallback((mode) => {
    if (contextMenu && contextMenu.column && contextMenu.value != null) {
      addFilter({ column: contextMenu.column, op: 'equals', value: String(contextMenu.value), mode });
    }
    handleCloseMenu();
  }, [contextMenu, addFilter, handleCloseMenu]);

  // Handle event selection and show details panel
  const handleEventSelection = useCallback((index) => {
    setSelectedIdx(index);
    setShowEventDetailsPanel(true);
  }, [setSelectedIdx, setShowEventDetailsPanel]);

  // Handle closing details panel
  const handleCloseDetailsPanel = useCallback(() => {
    setShowEventDetailsPanel(false);
  }, [setShowEventDetailsPanel]);

  // Handle navigating to previous event
  const handlePreviousEvent = useCallback(() => {
    if (selectedIdx !== null && selectedIdx > 0) {
      handleEventSelection(selectedIdx - 1);
    }
  }, [selectedIdx, handleEventSelection]);

  // Handle navigating to next event
  const handleNextEvent = useCallback(() => {
    if (selectedIdx !== null && selectedIdx < displayRows.length - 1) {
      handleEventSelection(selectedIdx + 1);
    }
  }, [selectedIdx, displayRows.length, handleEventSelection]);

  // Auto-scroll to selected item when it changes
  useEffect(() => {
    if (selectedIdx !== null && listRef.current) {
      // react-window v2 List imperative API
      listRef.current.scrollToRow({ index: selectedIdx, align: 'smart' });
    }
  }, [selectedIdx]);

  // Keyboard navigation for large datasets
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!displayRows.length) return;

      const currentIdx = selectedIdx ?? -1;
      let newIdx = currentIdx;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          newIdx = Math.min(currentIdx + 1, displayRows.length - 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          newIdx = Math.max(currentIdx - 1, 0);
          break;
        case 'PageDown':
          event.preventDefault();
          newIdx = Math.min(currentIdx + 10, displayRows.length - 1);
          break;
        case 'PageUp':
          event.preventDefault();
          newIdx = Math.max(currentIdx - 10, 0);
          break;
        case 'Home':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            newIdx = 0;
          }
          break;
        case 'End':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            newIdx = displayRows.length - 1;
          }
          break;
        default:
          return;
      }

      if (newIdx !== currentIdx) {
        handleEventSelection(newIdx);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedIdx, displayRows.length, handleEventSelection]);


  const handleDetailsResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = detailsWidth;

    const handleMouseMove = (moveE) => {
      const newWidth = Math.max(240, Math.min(startWidth - (moveE.clientX - startX), 800));
      setDetailsWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [detailsWidth]);

  // Memoized data for virtualized list - selectedIdxRef is stable, so this doesn't change on selection
  const listData = useMemo(() => ({
    displayRows: displayRows || [],
    selectedIdxRef,  // Pass ref instead of value - rows subscribe to changes
    onSelect: handleEventSelection,
    handleContextMenu,
    columns,
    formatOffset,
    colors: {
      selectedBg: theme.palette.action.selected,
      divider: theme.palette.divider,
      textPrimary: theme.palette.text.primary,
    }
  }), [displayRows, handleEventSelection, handleContextMenu, formatOffset, theme.palette.action.selected, theme.palette.divider, theme.palette.text.primary]);

  // Container ref and size for the virtualized list
  const rootRef = useRef(null);  // Outermost container to detect parent resize
  const containerRef = useRef(null);
  const listContainerRef = useRef(null);
  const [listHeight, setListHeight] = React.useState(400);
  const listHeightRef = useRef(listHeight);
  listHeightRef.current = listHeight;  // Keep ref in sync with state

  // Container ref and size for the event details panel
  const detailsContainerRef = useRef(null);
  const [detailsHeight, setDetailsHeight] = React.useState(400);

  // Measure the list container and update height for react-window
  useEffect(() => {
    const updateHeights = () => {
      // Use getBoundingClientRect for more accurate measurements in flex layouts
      if (listContainerRef.current) {
        const rect = listContainerRef.current.getBoundingClientRect();
        const height = Math.floor(rect.height);
        if (height > 0 && height !== listHeightRef.current) {
          setListHeight(height);
        }
      }
      if (detailsContainerRef.current) {
        const rect = detailsContainerRef.current.getBoundingClientRect();
        const height = Math.floor(rect.height);
        if (height > 0) {
          setDetailsHeight(height);
        }
      }
    };

    // Initial measurement
    updateHeights();
    
    // For large datasets, the layout might not be ready on first render.
    // Schedule additional measurements to catch late layout updates.
    const rafId = requestAnimationFrame(() => {
      updateHeights();
      // One more delayed measurement for cases where layout settles slowly
      setTimeout(updateHeights, 100);
    });

    // Use ResizeObserver to detect container size changes
    // Observe both the flex container and the list container since resize
    // events on the parent (e.g., dashboard panel drag) may not trigger
    // the child's ResizeObserver immediately
    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver((entries) => {
        // ResizeObserver fires with entry.contentRect - use it directly if available
        for (const entry of entries) {
          if (entry.target === listContainerRef.current && entry.contentRect) {
            const height = Math.floor(entry.contentRect.height);
            if (height > 0 && height !== listHeightRef.current) {
              setListHeight(height);
            }
          }
        }
        // Also do a full update in case other elements need measuring
        requestAnimationFrame(updateHeights);
      });
      // Observe all containers in the hierarchy to catch resizes at any level
      if (rootRef.current) {
        resizeObserver.observe(rootRef.current);
      }
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      if (listContainerRef.current) {
        resizeObserver.observe(listContainerRef.current);
      }
      if (detailsContainerRef.current) {
        resizeObserver.observe(detailsContainerRef.current);
      }
    }

    // Also listen to window resize
    window.addEventListener('resize', updateHeights);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateHeights);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  // Re-run when data loads or panel visibility changes - layout may change
  // Using displayRows.length triggers re-measurement when data loads
  }, [showEventDetailsPanel, displayRows.length]);

  // Download the full watch log (all raw events) as watch_log.jsonl (one JSON object per line).
  const handleDownloadWatchLog = useCallback(() => {
    try {
      const rows = Array.isArray(allRows) ? allRows : [];
      const jsonl = rows.map((r) => JSON.stringify(r)).join('\n');
      const blob = new Blob([jsonl], { type: 'application/jsonl' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'watch_log.jsonl';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so the browser commits the download before the blob URL is freed.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to download watch log:', err);
    }
  }, [allRows]);

  return (
    <Box ref={rootRef} display="flex" flexDirection="column" width="100%" maxWidth="100%" height="100%" sx={{ overflow: 'hidden', minHeight: 0 }}>
      {/* Controls bar */}
      <Box display="flex" alignItems="center" gap={0.5} sx={{ bgcolor: 'background.paper', borderRadius: 0, py: 0.25, px: 1, flexShrink: 0 }}>
        <Checkbox
          checked={showOnlyCurrentSecond}
          onChange={e => setShowOnlyCurrentSecond(e.target.checked)}
          size="small"
          sx={{ p: 0 }}
        />
        <Typography variant="body2" component="span" sx={{ fontSize: 11 }}>
          Only show events at current video second
        </Typography>
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', color: 'primary.main' }}>
            {displayRows.length.toLocaleString()} events
          </Typography>
          {Array.isArray(allRows) && allRows.length > displayRows.length && (
            <Typography variant="caption" sx={{ fontSize: 11, whiteSpace: 'nowrap' }} color="text.secondary">
              (of {allRows.length.toLocaleString()})
            </Typography>
          )}
          <Button
            size="small"
            onClick={handleDownloadWatchLog}
            variant="outlined"
            disabled={loading || !allRows || allRows.length === 0}
            title="Download watch_log.jsonl"
            sx={{ height: 32, minHeight: 32, minWidth: 32, px: 0.5, py: 0, flexShrink: 0 }}
          >
            <DownloadIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>

      <Box display="flex" gap={0} width="100%" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box 
          sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
        >

          <Box ref={containerRef} sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Paper sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Fixed Header Table */}
              <TableContainer sx={{ borderBottom: 2, borderColor: 'divider', flexShrink: 0 }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ height: ROW_HEIGHT }}>
                      {columns.map(col => (
                        <TableCell
                          key={col.id}
                          sx={{
                            p: 0.25,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                            userSelect: 'none',
                            width: col.id === 'Details' ? '25%' : col.id === 'ItemName' ? '20%' : '11%',
                            bgcolor: 'background.default',
                            fontWeight: 'bold',
                            borderBottom: 'none',
                            height: ROW_HEIGHT,
                          }}
                          onClick={() => {
                            if (sortBy === col.id) {
                              setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
                            } else {
                              setSortBy(col.id);
                              setSortDir('asc');
                            }
                          }}
                        >
                          <b>{col.label}</b>
                          {sortBy === col.id ? (
                            sortDir === 'asc' ? <ArrowDropUpIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> : <ArrowDropDownIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                          ) : (
                            <UnfoldMoreIcon fontSize="small" sx={{ verticalAlign: 'middle', opacity: 0.4 }} />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>

              {/* Virtualized Scrollable Body */}
              <Box 
                ref={listContainerRef}
                sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}
              >
                {displayRows.length > 0 ? (
                  <List
                    listRef={listRef}
                    rowCount={displayRows.length}
                    rowHeight={ROW_HEIGHT}
                    rowProps={listData}
                    rowComponent={VirtualTableRow}
                    overscanCount={BUFFER_SIZE}
                    style={{ width: '100%', height: Math.max(listHeight, ROW_HEIGHT * 10) }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                    <Typography>No events to display</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Simple loading fallback (for basic loading states) */}
            {loading && loadingProgress.phase === 'idle' && (
              <Box sx={{
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                bgcolor: 'background.paper',
                opacity: 0.6,
                zIndex: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CircularProgress size={48} />
              </Box>
            )}
            {error && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(255,0,0,0.1)', zIndex: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="error">{String(error)}</Typography>
              </Box>
            )}
            <Menu
              open={contextMenu !== null}
              onClose={handleCloseMenu}
              anchorReference="anchorPosition"
              anchorPosition={
                contextMenu !== null
                  ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                  : undefined
              }
            >
              <MenuItem onClick={() => handleAddFilter('include')}>Add to include filter</MenuItem>
              <MenuItem onClick={() => handleAddFilter('exclude')}>Add to exclude filter</MenuItem>
            </Menu>
          </Box>
        </Box>
        {showEventDetailsPanel && (
          <Box sx={{ m: 0.5 }}>
            <ResizeHandle direction="horizontal" onMouseDown={handleDetailsResizeMouseDown} />
          </Box>
        )}
        {showEventDetailsPanel && (
          <Box 
            sx={{ 
              width: detailsWidth,
              flexShrink: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              maxHeight: '100%',
            }}
          >
            <Paper 
              sx={{ 
                p: 0, 
                width: '100%', 
                flex: 1,
                display: 'flex', 
                flexDirection: 'column', 
                minHeight: 0,
                maxHeight: '100%',
              }}
            >
              {/* Fixed header for details panel */}
              <Box sx={{ 
                bgcolor: 'background.paper', 
                borderRadius: 0, 
                py: 0.5, 
                px: 1, 
                boxShadow: 0, 
                borderBottom: 1, 
                borderColor: 'divider', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexShrink: 0,
              }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.3, textAlign: 'left' }}
                >
                  {selectedEvent ? 'Event Details' : 'Select an event'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <Tooltip title="Previous event">
                    <IconButton
                      size="small"
                      onClick={handlePreviousEvent}
                      disabled={selectedIdx === null || selectedIdx === 0}
                      sx={{ color: 'primary.main', p: 0.25 }}
                    >
                      <ArrowCircleUpIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Next event">
                    <IconButton
                      size="small"
                      onClick={handleNextEvent}
                      disabled={selectedIdx === null || selectedIdx === displayRows.length - 1}
                      sx={{ color: 'primary.main', p: 0.25 }}
                    >
                      <ArrowCircleDownIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={handleCloseDetailsPanel}
                    sx={{ color: 'text.secondary', p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
              {/* Scrollable content area for details */}
              <Box 
                ref={detailsContainerRef}
                sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}
              >
                <Box sx={{ p: 1, overflow: 'auto', height: detailsHeight, maxHeight: '100%' }}>
                {selectedEvent ? (
                  <>
                    <Box mb={1}>
                      {typeof selectedEvent.VideoOffset === 'number' && !isNaN(selectedEvent.VideoOffset) && (
                        <Box mb={0.5} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 11, color: 'primary.main', textAlign: 'left' }}>
                            Time: {formatOffset(selectedEvent.VideoOffset)}
                          </Typography>
                          <Tooltip title="Jump to in video">
                            <IconButton
                              size="small"
                              onClick={() => setSeekTo(selectedEvent.VideoOffset / 1000)}
                              sx={{ color: 'primary.main', p: 0.25 }}
                            >
                              <SmartDisplayOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Display click image if available - use memoized lookup */}
                    {/* Defer loading for large files to improve responsiveness */}
                    <MemoizedClickImage 
                      selectedEvent={selectedEvent}
                      mouseClickRowsWithImages={mouseClickRowsWithImages}
                      clickimagesPath={appInfo?.clickimagesPath}
                      deferImageLoad={hugeLogMode}
                    />
                    
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, fontSize: 13, wordBreak: 'break-all' }}>
                      {Object.entries(selectedEvent).map(([key, value]) => {
                        if (key === 'VideoOffset' && typeof value === 'number' && !isNaN(value)) {
                          return (
                            <Box component="li" key={key} sx={{ mb: 0.5, display: 'flex', gap: 1, alignItems: 'flex-start', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                              onContextMenu={e => handleContextMenu(e, key, value)}
                            >
                              <b style={{ minWidth: 110, display: 'inline-block', textAlign: 'left' }}>{key}:</b> <span>{value} ms</span>
                            </Box>
                          );
                        }
                        return (
                          <Box component="li" key={key} sx={{ mb: 0.5, display: 'flex', gap: 1, alignItems: 'flex-start', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                            onContextMenu={e => handleContextMenu(e, key, value)}
                          >
                            <b style={{ minWidth: 110, display: 'inline-block', textAlign: 'left' }}>{key}:</b> <span>{typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}</span>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">Click a row to see all fields.</Typography>
                )}
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WatchLogViewer;
